import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { AppConfig, WindowState } from "./types";
import { layout } from "./theme";

interface WindowManagerValue {
  registry: AppConfig[];
  windows: WindowState[];
  focusedId: string | null;
  getApp: (appId: string) => AppConfig | undefined;
  isOpen: (appId: string) => boolean;
  openApp: (appId: string) => void;
  closeWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  toggleMaximize: (id: string) => void;
  closeAll: () => void;
  moveWindow: (id: string, x: number, y: number) => void;
  resizeWindow: (
    id: string,
    pos: { x: number; y: number },
    size: { width: number; height: number }
  ) => void;
}

const WindowManagerContext = createContext<WindowManagerValue | null>(null);

const BASE_Z = 10;

export const WindowManagerProvider = ({
  registry,
  bootEnabled = true,
  children,
}: {
  registry: AppConfig[];
  bootEnabled?: boolean;
  children: React.ReactNode;
}) => {
  const [windows, setWindows] = useState<WindowState[]>([]);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const topZ = useRef(BASE_Z);
  const openCount = useRef(0);

  const getApp = useCallback(
    (appId: string) => registry.find((a) => a.id === appId),
    [registry]
  );

  const focusWindow = useCallback((id: string) => {
    topZ.current += 1;
    const nextZ = topZ.current;
    setWindows((prev) =>
      prev.map((w) =>
        w.id === id ? { ...w, z: nextZ, minimized: false } : w
      )
    );
    setFocusedId(id);
  }, []);

  const openApp = useCallback(
    (appId: string) => {
      const app = getApp(appId);
      if (!app) return;

      const single = app.single !== false;
      if (single) {
        const existing = windows.find((w) => w.appId === appId);
        if (existing) {
          focusWindow(existing.id);
          return;
        }
      }

      const { width, height } = app.defaultSize;
      const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
      const vh = typeof window !== "undefined" ? window.innerHeight : 800;
      const cascade = (openCount.current % 6) * 28;
      const x = Math.max(
        16,
        Math.round((vw - width) / 2) + cascade - 84
      );
      const y = Math.max(
        layout.menuBarHeight + 16,
        Math.round((vh - height) / 2) + cascade - 60
      );

      topZ.current += 1;
      openCount.current += 1;
      const id = `${appId}-${openCount.current}`;

      setWindows((prev) => [
        ...prev,
        {
          id,
          appId,
          x,
          y,
          width,
          height,
          z: topZ.current,
          minimized: false,
          maximized: false,
        },
      ]);
      setFocusedId(id);
    },
    [getApp, windows, focusWindow]
  );

  const closeWindow = useCallback((id: string) => {
    setWindows((prev) => prev.filter((w) => w.id !== id));
    setFocusedId((curr) => (curr === id ? null : curr));
  }, []);

  const closeAll = useCallback(() => {
    setWindows([]);
    setFocusedId(null);
  }, []);

  const minimizeWindow = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, minimized: true } : w))
    );
    setFocusedId((curr) => (curr === id ? null : curr));
  }, []);

  const toggleMaximize = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((w) => {
        if (w.id !== id) return w;
        if (w.maximized && w.restore) {
          return { ...w, maximized: false, ...w.restore, restore: undefined };
        }
        return {
          ...w,
          maximized: true,
          restore: { x: w.x, y: w.y, width: w.width, height: w.height },
        };
      })
    );
    focusWindow(id);
  }, [focusWindow]);

  const moveWindow = useCallback((id: string, x: number, y: number) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, x, y } : w))
    );
  }, []);

  const resizeWindow = useCallback(
    (
      id: string,
      pos: { x: number; y: number },
      size: { width: number; height: number }
    ) => {
      setWindows((prev) =>
        prev.map((w) =>
          w.id === id
            ? { ...w, x: pos.x, y: pos.y, width: size.width, height: size.height }
            : w
        )
      );
    },
    []
  );

  const isOpen = useCallback(
    (appId: string) => windows.some((w) => w.appId === appId),
    [windows]
  );

  // Escape closes the currently focused window.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && focusedId) {
        closeWindow(focusedId);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [focusedId, closeWindow]);

  // Auto-open any boot apps once on mount.
  const booted = useRef(false);
  useEffect(() => {
    if (booted.current || !bootEnabled) return;
    booted.current = true;
    registry
      .filter((a) => a.openOnBoot)
      .forEach((a) => openApp(a.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootEnabled]);

  const value = useMemo<WindowManagerValue>(
    () => ({
      registry,
      windows,
      focusedId,
      getApp,
      isOpen,
      openApp,
      closeWindow,
      focusWindow,
      minimizeWindow,
      toggleMaximize,
      closeAll,
      moveWindow,
      resizeWindow,
    }),
    [
      registry,
      windows,
      focusedId,
      getApp,
      isOpen,
      openApp,
      closeWindow,
      focusWindow,
      minimizeWindow,
      toggleMaximize,
      closeAll,
      moveWindow,
      resizeWindow,
    ]
  );

  return (
    <WindowManagerContext.Provider value={value}>
      {children}
    </WindowManagerContext.Provider>
  );
};

export const useWindowManager = (): WindowManagerValue => {
  const ctx = useContext(WindowManagerContext);
  if (!ctx) {
    throw new Error("useWindowManager must be used within a WindowManagerProvider");
  }
  return ctx;
};
